import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App as AntApp,
  Avatar,
  Button,
  Card,
  ConfigProvider,
  Divider,
  Form,
  Input,
  Modal,
  Slider,
  Space,
  Spin,
  Tag,
  Typography,
  theme,
} from "antd";
import zhCN from "antd/locale/zh_CN";

import { authClient, issuer } from "../auth-client.ts";
import { normalizeAvatarUrl } from "../avatar-url.ts";
import {
  canOfferOtherAccountAction,
  handleAccountCardKeyDown,
  runExclusiveAccountAction,
} from "./account-card-activation.ts";
import { resolveChallengeAdapter } from "./challenge-adapter.ts";
import { AuthSliderChallenge } from "./AuthSliderChallenge.tsx";
import {
  emailSignInBody,
  loadTrustedOAuthClient,
  localizedAuthError,
  registrationStatusMessage,
  requestJson,
} from "./http-ui.ts";
import {
  bindEmailDestination,
  loginAttemptOrder,
  maskEmail,
  oauthQueryFromLocation,
  recoveryErrorMessage,
  resolveAuthPage,
} from "./routing.ts";
import {
  registrationIdentityChanged,
  type RegistrationEmailCodeRequest,
} from "./registration-flow.ts";
import "../styles/auth.css";

function navigate(path: string) {
  window.location.assign(path);
}

function ChallengeField({ token, onToken }: { token: string; onToken(token: string): void }) {
  const adapter = resolveChallengeAdapter({
    production: import.meta.env.PROD,
    developmentStaticEnabled:
      import.meta.env.VITE_AUTH_DEVELOPMENT_STATIC_CHALLENGE === "true",
  });
  if (adapter.mode === "development-static") {
    return (
      <Slider
        value={token ? 100 : 0}
        onChange={(value) => onToken(value === 100 ? "slider-complete" : "")}
        tooltip={{ open: false }}
        aria-label="开发滑块验证"
      />
    );
  }
  return <AuthSliderChallenge token={token} onVerified={onToken} />;
}

function useSystemDarkMode() {
  const [dark, setDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => setDark(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);
  return dark;
}

function BrandLayout({ children, eyebrow }: { children: React.ReactNode; eyebrow: string }) {
  return (
    <main className="auth-canvas">
      <div className="poster poster-top" aria-hidden="true">UNIFIED ACCOUNT</div>
      <div className="poster poster-side" aria-hidden="true">GBC / AUTH</div>
      <section className="auth-shell">
        <header className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">G</span>
          <div>
            <Typography.Text className="brand-name">GBC 同好站</Typography.Text>
            <Typography.Title level={1}>GBC 统一账号</Typography.Title>
          </div>
        </header>
        <Typography.Text className="eyebrow">{eyebrow}</Typography.Text>
        {children}
      </section>
      <div className="poster poster-bottom" aria-hidden="true">SECURE / SIMPLE / YOURS</div>
    </main>
  );
}

function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const query = window.location.search;
  const queryParams = new URLSearchParams(query);
  const boundEmail = queryParams.get("email") ?? undefined;
  return (
    <BrandLayout eyebrow="SIGN IN">
      <Typography.Paragraph className="intro">使用统一账号继续访问 GBC 服务。</Typography.Paragraph>
      {queryParams.get("notice") === "email-bound" ? (
        <Alert type="warning" showIcon message="邮箱已绑定，请使用新邮箱重新登录" />
      ) : null}
      {queryParams.get("notice") === "password-changed" ? (
        <Alert type="success" showIcon message="密码已修改，请重新登录" />
      ) : null}
      {queryParams.get("notice") === "password-reset" ? (
        <Alert type="success" showIcon message="密码已重置，请使用新密码登录" />
      ) : null}
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Form
        {...(boundEmail ? { initialValues: { identifier: boundEmail } } : {})}
        layout="vertical"
        requiredMark={false}
        onFinish={async (values: { identifier: string; password: string }) => {
          setLoading(true);
          setError(undefined);
          try {
            const oauthQuery = oauthQueryFromLocation(query);
            const legacy = await requestJson<{ next: string }>(() =>
              fetch(`${issuer}/legacy-login`, {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({
                  username: values.identifier,
                  password: values.password,
                  ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
                }),
              }),
            );
            if (legacy.ok) {
              if (legacy.data.next === "bind-email") {
                navigate(`/bind-email${query}`);
                return;
              }
            } else if (loginAttemptOrder(values.identifier).includes("email")) {
              const result = await requestJson(() =>
                fetch(`${issuer}/sign-in/email`, {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "content-type": "application/json",
                    "x-requested-with": "XMLHttpRequest",
                  },
                  body: JSON.stringify(
                    emailSignInBody(values.identifier, values.password, oauthQuery),
                  ),
                }),
              );
              if (!result.ok) throw new Error(localizedAuthError("INVALID_CREDENTIALS"));
            } else {
              throw new Error(localizedAuthError("INVALID_CREDENTIALS"));
            }
            navigate(oauthQuery ? `/select-account?oauth_query=${encodeURIComponent(oauthQuery)}` : "/account");
          } catch (caught) {
            setError(localizedAuthError(caught, "LOGIN_UNAVAILABLE"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item name="identifier" label="邮箱或历史用户名" rules={[{ required: true, message: "请输入账号" }]}>
          <Input autoComplete="username" size="large" />
        </Form.Item>
        <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }]}>
          <Input.Password autoComplete="current-password" size="large" />
        </Form.Item>
        <Button htmlType="submit" type="primary" size="large" block loading={loading}>登录</Button>
      </Form>
      <Space split={<Divider type="vertical" />} className="auth-links">
        <Button type="link" onClick={() => navigate("/register")}>注册账号</Button>
        <Button type="link" onClick={() => navigate("/forgot-password")}>找回密码</Button>
      </Space>
    </BrandLayout>
  );
}

function RegisterPage() {
  const [form] = Form.useForm();
  const [challengeToken, setChallengeToken] = useState("");
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [pendingEmailCodeRequest, setPendingEmailCodeRequest] = useState<RegistrationEmailCodeRequest>();
  const [emailCodeRequest, setEmailCodeRequest] = useState<RegistrationEmailCodeRequest>();
  const [sendingCode, setSendingCode] = useState(false);
  const emailCodeSendInFlight = useRef(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>();
  const [messageType, setMessageType] = useState<"error" | "info" | "success">("info");

  const sendRegistrationEmailCode = async (verifiedToken: string) => {
    if (!verifiedToken || !pendingEmailCodeRequest || emailCodeSendInFlight.current) return;
    emailCodeSendInFlight.current = true;
    setSendingCode(true);
    try {
      const result = await requestJson(() =>
        fetch(`${issuer}/registration/email-code`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            purpose: "registration",
            email: pendingEmailCodeRequest.email,
            display_name: pendingEmailCodeRequest.displayName,
            challenge_token: verifiedToken,
          }),
        }),
      );
      if (!result.ok) throw new Error(localizedAuthError(
        result.error,
        "VERIFICATION_CODE_SEND_FAILED",
      ));
      setEmailCodeRequest(pendingEmailCodeRequest);
      setMessage(undefined);
    } catch (error) {
      setMessageType("error");
      setMessage(localizedAuthError(error, "VERIFICATION_CODE_SEND_FAILED"));
    } finally {
      emailCodeSendInFlight.current = false;
      setSendingCode(false);
      setChallengeOpen(false);
      setPendingEmailCodeRequest(undefined);
      setChallengeToken("");
    }
  };

  return (
    <BrandLayout eyebrow="CREATE ACCOUNT">
      {message ? <Alert type={messageType} showIcon message={message} /> : null}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onValuesChange={(_, values: Record<string, string>) => {
          if (!registrationIdentityChanged(emailCodeRequest, values)) return;
          setEmailCodeRequest(undefined);
          setChallengeToken("");
          setMessageType("info");
          setMessage("邮箱或展示昵称已修改，请重新发送验证码");
        }}
        onFinish={async (values: Record<string, string>) => {
          setLoading(true);
          try {
            const result = await requestJson<{ status?: string }>(() =>
              fetch(`${issuer}/registration`, {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(values),
              }),
            );
            if (!result.ok) {
              setMessageType("error");
              setMessage(localizedAuthError(result.error, "REGISTRATION_FAILED"));
              return;
            }
            setMessageType(result.data.status === "active" ? "success" : result.data.status === "disabled" ? "error" : "info");
            setMessage(registrationStatusMessage(result.data.status));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item name="email" label="邮箱" rules={[
          { required: true, message: "请输入邮箱" },
          { type: "email", message: "邮箱格式不正确" },
        ]}>
          <Input autoComplete="email" size="large" />
        </Form.Item>
        <Form.Item name="display_name" label="展示昵称" rules={[
          { required: true, message: "请输入展示昵称" },
          { max: 64, message: "展示昵称最多为 64 个字符" },
        ]}>
          <Input autoComplete="nickname" size="large" />
        </Form.Item>
        <Form.Item name="verification_code" label="邮箱验证码" rules={[
          { required: true, message: "请输入邮箱验证码" },
          { len: 6, message: "邮箱验证码应为 6 位数字" },
        ]}>
          <Input inputMode="numeric" autoComplete="one-time-code" size="large" />
        </Form.Item>
        <div className="registration-code-action">
          <Button
            block
            loading={sendingCode}
            onClick={async () => {
              const values = await form.validateFields(["email", "display_name"])
                .catch(() => undefined);
              if (!values) return;
              setPendingEmailCodeRequest({
                email: values.email,
                displayName: values.display_name,
              });
              setChallengeToken("");
              setMessage(undefined);
              setChallengeOpen(true);
            }}
          >
            {emailCodeRequest ? "重新发送邮箱验证码" : "发送邮箱验证码"}
          </Button>
          <div className="registration-code-status" role="status">
            {emailCodeRequest
              ? `验证码已发送至 ${emailCodeRequest.email}`
              : "点击发送后，将弹出滑块验证"}
          </div>
        </div>
        <Form.Item name="password" label="密码" rules={[
          { required: true, message: "请输入密码" },
          { min: 10, message: "密码至少为 10 个字符" },
        ]}>
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>
        <Button htmlType="submit" type="primary" size="large" block loading={loading}>创建统一账号</Button>
      </Form>
      <Button type="link" block onClick={() => navigate("/login")}>已有账号，返回登录</Button>
      <Modal
        className="registration-challenge-modal"
        open={challengeOpen}
        title="请完成滑块验证"
        footer={null}
        width={380}
        centered
        destroyOnHidden
        closable={!sendingCode}
        maskClosable={!sendingCode}
        keyboard={!sendingCode}
        onCancel={() => {
          if (sendingCode) return;
          setChallengeOpen(false);
          setPendingEmailCodeRequest(undefined);
          setChallengeToken("");
        }}
      >
        <Typography.Paragraph className="registration-challenge-help">
          验证通过后，系统会自动发送邮箱验证码。
        </Typography.Paragraph>
        <ChallengeField
          token={challengeToken}
          onToken={(token) => {
            setChallengeToken(token);
            if (token) void sendRegistrationEmailCode(token);
          }}
        />
        <div className="registration-challenge-status" aria-live="polite">
          {sendingCode ? "验证通过，正在发送验证码…" : "请拖动滑块完成拼图"}
        </div>
      </Modal>
    </BrandLayout>
  );
}

function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  return (
    <BrandLayout eyebrow="RECOVERY">
      <Typography.Paragraph className="intro">输入已绑定邮箱。</Typography.Paragraph>
      <div aria-live="polite">
        {message ? <Alert type="success" showIcon message={message} /> : null}
        {error ? <Alert type="error" showIcon message={error} /> : null}
      </div>
      <Form
        layout="vertical"
        requiredMark={false}
        onFinish={async (values: { email: string }) => {
          setLoading(true);
          setMessage(undefined);
          setError(undefined);
          try {
            const result = await requestJson<{ status: "accepted" }>(() =>
              fetch(`${issuer}/password-recovery/request`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "content-type": "application/json",
                  "x-requested-with": "XMLHttpRequest",
                },
                body: JSON.stringify({ email: values.email }),
              }),
            );
            if (!result.ok) throw new Error(recoveryErrorMessage(result.error));
            setMessage("如果该邮箱已绑定账号，重置邮件会在几分钟内送达");
          } catch (caught) {
            setError(localizedAuthError(caught, "PASSWORD_RECOVERY_FAILED"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item
          name="email"
          label="绑定邮箱"
          rules={[{ required: true, message: "请输入邮箱" }, { type: "email", message: "邮箱格式不正确" }]}
        >
          <Input autoComplete="email" inputMode="email" size="large" autoFocus />
        </Form.Item>
        <Button htmlType="submit" type="primary" size="large" block loading={loading}>
          发送重置邮件
        </Button>
      </Form>
      <Button type="link" block onClick={() => navigate("/login")}>返回登录</Button>
    </BrandLayout>
  );
}

function ResetPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [complete, setComplete] = useState(false);
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") ?? "", []);
  if (!token) {
    return (
      <BrandLayout eyebrow="RESET PASSWORD">
        <Alert type="error" showIcon message={localizedAuthError("INVALID_RESET_TOKEN")} />
        <Button type="link" block onClick={() => navigate("/forgot-password")}>重新申请</Button>
      </BrandLayout>
    );
  }
  if (complete) {
    return (
      <BrandLayout eyebrow="RESET PASSWORD">
        <Alert type="success" showIcon message="密码已重置，所有设备均已退出登录" />
        <Button type="primary" block onClick={() => navigate("/login?notice=password-reset")}>
          使用新密码登录
        </Button>
      </BrandLayout>
    );
  }
  return (
    <BrandLayout eyebrow="RESET PASSWORD">
      <Typography.Paragraph className="intro">
        新密码生效后，所有登录会话都会取消。
      </Typography.Paragraph>
      <div aria-live="assertive">
        {error ? <Alert type="error" showIcon message={error} /> : null}
      </div>
      <Form
        layout="vertical"
        requiredMark={false}
        onFinish={async (values: { newPassword: string; confirmPassword: string }) => {
          if (values.newPassword !== values.confirmPassword) {
            setError("两次输入的新密码不一致");
            return;
          }
          setLoading(true);
          setError(undefined);
          try {
            const result = await requestJson<{ success: true }>(() =>
              fetch(`${issuer}/password-recovery/reset`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "content-type": "application/json",
                  "x-requested-with": "XMLHttpRequest",
                },
                body: JSON.stringify({ token, newPassword: values.newPassword }),
              }),
            );
            if (!result.ok) throw new Error(recoveryErrorMessage(result.error));
            window.history.replaceState(null, "", "/reset-password?status=complete");
            setComplete(true);
          } catch (caught) {
            setError(localizedAuthError(caught, "PASSWORD_RECOVERY_FAILED"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[{ required: true, message: "请输入新密码" }, { min: 10, max: 128, message: "密码长度应为 10 到 128 个字符" }]}
        >
          <Input.Password autoComplete="new-password" size="large" autoFocus />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认新密码"
          rules={[{ required: true, message: "请再次输入新密码" }]}
        >
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>
        <Button htmlType="submit" type="primary" block size="large" loading={loading}>
          重置密码并退出所有设备
        </Button>
      </Form>
    </BrandLayout>
  );
}

function BindEmailPage() {
  const [form] = Form.useForm();
  const [challengeToken, setChallengeToken] = useState("");
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  return (
    <BrandLayout eyebrow="COMPLETE ACCOUNT">
      <Alert type="warning" showIcon message="验证旧用户名后，必须绑定邮箱才能继续授权" />
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Form
        form={form}
        layout="vertical"
        onFinish={async (values: Record<string, string>) => {
          setLoading(true);
          try {
            const result = await requestJson<{
              next: string;
              oauth_query: string | null;
            }>(() =>
              fetch(`${issuer}/bind-email`, {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(values),
              }),
            );
            if (!result.ok) {
              setError(localizedAuthError(result.error, "EMAIL_BINDING_FAILED"));
              return;
            }
            navigate(bindEmailDestination(result.data.next, result.data.oauth_query, values.email));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item name="email" label="新邮箱" rules={[
          { required: true, message: "请输入新邮箱" },
          { type: "email", message: "邮箱格式不正确" },
        ]}>
          <Input autoComplete="email" size="large" />
        </Form.Item>
        <Form.Item name="verification_code" label="验证码" rules={[
          { required: true, message: "请输入验证码" },
          { len: 6, message: "验证码应为 6 位数字" },
        ]}>
          <Input autoComplete="one-time-code" size="large" />
        </Form.Item>
        <Button
          block
          disabled={!challengeToken}
          onClick={async () => {
            try {
              const { email } = await form.validateFields(["email"]);
              const result = await requestJson(() =>
                fetch(`${issuer}/registration/email-code`, {
                  method: "POST",
                  credentials: "include",
                  headers: { "content-type": "application/json" },
                  body: JSON.stringify({
                    purpose: "email_binding",
                    email,
                    challenge_token: challengeToken,
                  }),
                }),
              );
              if (!result.ok) throw new Error(localizedAuthError(
                result.error,
                "VERIFICATION_CODE_SEND_FAILED",
              ));
              setError(undefined);
              setChallengeToken("");
            } catch (caught) {
              setError(localizedAuthError(caught, "VERIFICATION_CODE_SEND_FAILED"));
            }
          }}
        >
          发送验证码
        </Button>
        <Form.Item label="人机验证">
          <ChallengeField token={challengeToken} onToken={setChallengeToken} />
        </Form.Item>
        <Button htmlType="submit" type="primary" block size="large" loading={loading}>绑定并继续</Button>
      </Form>
    </BrandLayout>
  );
}

function SelectAccountPage() {
  const session = authClient.useSession();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [trustedClient, setTrustedClient] = useState<{ clientId: string; clientName: string }>();
  const [clientLoading, setClientLoading] = useState(true);
  const actionInFlight = useRef(false);
  const oauthQuery = oauthQueryFromLocation(window.location.search);
  useEffect(() => {
    let active = true;
    setClientLoading(true);
    setTrustedClient(undefined);
    if (!oauthQuery) {
      setActionError(localizedAuthError("AUTHORIZATION_REQUEST_INVALID"));
      setClientLoading(false);
      return () => { active = false; };
    }
    void loadTrustedOAuthClient({ issuer, oauthQuery })
      .then((client) => { if (active) setTrustedClient(client); })
      .catch(() => {
        if (active) setActionError(localizedAuthError("AUTHORIZATION_REQUEST_INVALID"));
      })
      .finally(() => { if (active) setClientLoading(false); });
    return () => { active = false; };
  }, [oauthQuery]);
  if (session.isPending || clientLoading) return <BrandLayout eyebrow="SELECT ACCOUNT"><Spin /></BrandLayout>;
  if (!session.data?.user) {
    navigate(oauthQuery ? `/login?oauth_query=${encodeURIComponent(oauthQuery)}` : "/login");
    return null;
  }
  const user = session.data.user;
  const activateAccount = async () => {
    await runExclusiveAccountAction(actionInFlight, async () => {
      setActionLoading(true);
      setActionError(undefined);
      try {
        await continueOAuth(oauthQuery);
      } catch (error) {
        setActionError(localizedAuthError(error, "AUTHORIZATION_FAILED"));
      } finally {
        setActionLoading(false);
      }
    });
  };
  return (
    <BrandLayout eyebrow="SELECT ACCOUNT">
      {trustedClient ? (
        <Typography.Paragraph className="intro">确认使用以下账号前往 {trustedClient.clientName}。</Typography.Paragraph>
      ) : null}
      {actionError ? <Alert type="error" showIcon message={actionError} /> : null}
      {trustedClient ? <Card
        className="account-card"
        hoverable
        role="button"
        tabIndex={0}
        aria-busy={actionLoading}
        onClick={() => void activateAccount()}
        onKeyDown={(event) =>
          handleAccountCardKeyDown(
            event,
            actionLoading || actionInFlight.current,
            () => void activateAccount(),
          )
        }
      >
        <Space size="middle">
          <Avatar size={56} src={normalizeAvatarUrl(user.image)}>{user.name.slice(0, 1)}</Avatar>
          <div>
            <Typography.Title level={3}>{user.name}</Typography.Title>
            <Typography.Text type="secondary">{maskEmail(user.email)}</Typography.Text>
          </div>
        </Space>
        <Tag color="red">{actionLoading ? "处理中" : "继续授权"}</Tag>
      </Card> : null}
      {canOfferOtherAccountAction(trustedClient) ? <Button
        block
        loading={actionLoading}
        disabled={actionLoading}
        onClick={() => void runExclusiveAccountAction(actionInFlight, async () => {
          setActionLoading(true);
          setActionError(undefined);
          try {
            await transactionalSignOut();
            navigate(oauthQuery ? `/login?oauth_query=${encodeURIComponent(oauthQuery)}` : "/login");
          } catch (error) {
            setActionError(localizedAuthError(error, "SIGN_OUT_FAILED"));
          } finally {
            setActionLoading(false);
          }
        })}
      >使用其他账号</Button> : null}
    </BrandLayout>
  );
}

async function transactionalSignOut(all = false) {
  const result = await requestJson<{ success: true }>(() =>
    fetch(`${issuer}/${all ? "sign-out-all" : "sign-out"}`, {
      method: "POST",
      credentials: "include",
      headers: { "x-requested-with": "XMLHttpRequest" },
    }),
  );
  if (!result.ok) throw new Error(localizedAuthError(result.error, "SIGN_OUT_FAILED"));
}

async function continueOAuth(oauthQuery: string) {
  const result = await requestJson<{ url?: string; redirect_uri?: string }>(() =>
    fetch(`${issuer}/oauth2/continue`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", "x-requested-with": "XMLHttpRequest" },
      body: JSON.stringify({ selected: true, oauth_query: oauthQuery }),
    }),
  );
  if (!result.ok) throw new Error(localizedAuthError(result.error, "AUTHORIZATION_FAILED"));
  const target = result.data.url ?? result.data.redirect_uri;
  if (!target) throw new Error(localizedAuthError("AUTHORIZATION_FAILED"));
  window.location.assign(target);
}

function ConsentPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const oauthQuery = oauthQueryFromLocation(window.location.search);
  return (
    <BrandLayout eyebrow="AUTHORIZE">
      <Typography.Title level={2}>允许访问账号信息？</Typography.Title>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {[true, false].map((accept) => (
        <Button
          key={String(accept)}
          type={accept ? "primary" : "default"}
          block
          size={accept ? "large" : "middle"}
          loading={loading}
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            setError(undefined);
            try {
              await consent(oauthQuery, accept);
            } catch (caught) {
              setError(localizedAuthError(caught, "AUTHORIZATION_FAILED"));
            } finally {
              setLoading(false);
            }
          }}
        >{accept ? "允许" : "拒绝"}</Button>
      ))}
    </BrandLayout>
  );
}

async function consent(oauthQuery: string, accept: boolean) {
  const result = await requestJson<{ url?: string; redirect_uri?: string }>(() =>
    fetch(`${issuer}/oauth2/consent`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accept, oauth_query: oauthQuery }),
    }),
  );
  if (!result.ok) throw new Error(localizedAuthError(result.error, "AUTHORIZATION_FAILED"));
  const target = result.data.url ?? result.data.redirect_uri;
  if (!target) throw new Error(localizedAuthError("AUTHORIZATION_RESPONSE_INVALID"));
  window.location.assign(target);
}

function AccountPage() {
  const session = authClient.useSession();
  if (session.isPending) return <BrandLayout eyebrow="ACCOUNT"><Spin /></BrandLayout>;
  if (!session.data?.user) return <LoginPage />;
  return (
    <BrandLayout eyebrow="ACCOUNT CENTER">
      <Card className="account-summary">
        <Space size="middle">
          <Avatar size={64} src={normalizeAvatarUrl(session.data.user.image)}>{session.data.user.name.slice(0, 1)}</Avatar>
          <div>
            <Typography.Title level={2}>{session.data.user.name}</Typography.Title>
            <Typography.Text>{session.data.user.email}</Typography.Text>
          </div>
        </Space>
      </Card>
      <Button
        block
        onClick={() => navigate("/change-password")}
      >
        修改密码
      </Button>
      <Button danger block onClick={async () => { await transactionalSignOut(); navigate("/login"); }}>退出登录</Button>
    </BrandLayout>
  );
}

function ChangePasswordPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <BrandLayout eyebrow="CHANGE PASSWORD">
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Form
        layout="vertical"
        onFinish={async (values: {
          currentPassword: string;
          newPassword: string;
          confirmPassword: string;
        }) => {
          if (values.newPassword !== values.confirmPassword) {
            setError("两次输入的新密码不一致");
            return;
          }
          setLoading(true);
          setError(undefined);
          try {
            const result = await requestJson<{ success: true }>(() =>
              fetch(`${issuer}/change-password`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "content-type": "application/json",
                  "x-requested-with": "XMLHttpRequest",
                },
                body: JSON.stringify({
                  currentPassword: values.currentPassword,
                  newPassword: values.newPassword,
                }),
              }),
            );
            if (!result.ok) throw new Error(recoveryErrorMessage(result.error));
            navigate("/login?notice=password-changed");
          } catch (caught) {
            setError(localizedAuthError(caught, "PASSWORD_CHANGE_FAILED"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item name="currentPassword" label="当前密码" rules={[
          { required: true, message: "请输入当前密码" },
        ]}>
          <Input.Password autoComplete="current-password" size="large" />
        </Form.Item>
        <Form.Item name="newPassword" label="新密码" rules={[
          { required: true, message: "请输入新密码" },
          { min: 10, message: "新密码至少为 10 个字符" },
          { max: 128, message: "新密码最多为 128 个字符" },
        ]}>
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>
        <Form.Item name="confirmPassword" label="确认新密码" rules={[
          { required: true, message: "请再次输入新密码" },
        ]}>
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>
        <Button htmlType="submit" type="primary" block loading={loading}>修改并退出所有设备</Button>
      </Form>
    </BrandLayout>
  );
}

export function App() {
  const dark = useSystemDarkMode();
  const page = resolveAuthPage(window.location.pathname);
  const content =
    page === "register" ? <RegisterPage /> :
    page === "forgot-password" ? <ForgotPasswordPage /> :
    page === "reset-password" ? <ResetPasswordPage /> :
    page === "bind-email" ? <BindEmailPage /> :
    page === "select-account" ? <SelectAccountPage /> :
    page === "consent" ? <ConsentPage /> :
    page === "change-password" ? <ChangePasswordPage /> :
    page === "account" ? <AccountPage /> : <LoginPage />;
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: { colorPrimary: "#df1636", borderRadius: 8, fontFamily: '"Noto Sans SC", "PingFang SC", sans-serif' },
      }}
    >
      <AntApp>{content}</AntApp>
    </ConfigProvider>
  );
}
